import React, { useState } from 'react';
import { IEventSummary, IShow } from '@eagerdog/interfaces';
import { Constants } from '@eagerdog/constants';

import { apiService } from 'src/services';
import { toast } from 'src/components/Toast/ToastManager';
import Checkbox from 'src/components/Checkbox/Checkbox';
import Button from 'src/components/Button/Button';

import styles from './DayOfShowTab.module.scss';

interface IProps {
  event: IEventSummary;
  show: IShow;
  onUpdate(): void;
}

const DayOfShowTab: React.FC<IProps> = ({ event, show, onUpdate }) => {
  const clubHasAccess = !!(event.club as any)?.has_pay_club_directly_access;
  const isRegistrationOrder =
    !event.armband_order_type ||
    event.armband_order_type === Constants.armband_order_type.registration_order;

  const [payClubDirectlyEnabled, setPayClubDirectlyEnabled] = useState<boolean>(
    show.pay_club_directly_enabled ?? false
  );
  const [autoAssignArmbandNumbers, setAutoAssignArmbandNumbers] = useState<boolean>(
    event.auto_assign_armband_numbers ?? true
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.updateShow(event._id, show._id, {
        ...show,
        pay_club_directly_enabled: payClubDirectlyEnabled,
      } as IShow);

      if (isRegistrationOrder) {
        const { __v: _eventVersion, ...eventWithoutVersion } = event as typeof event & { __v?: number };
        await apiService.updateEvent(event._id, {
          ...eventWithoutVersion,
          auto_assign_armband_numbers: autoAssignArmbandNumbers,
        } as any);
      }
      toast.show({
        title: 'Day of Show',
        content: 'Settings saved successfully.',
        duration: 5000,
        type: 'success',
      });
      onUpdate();
    } catch {
      toast.show({
        title: 'Day of Show',
        content: 'Failed to save settings. Please try again.',
        duration: 5000,
        type: 'fail',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.DayOfShowTab}>
      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <span className={styles.settingLabel}>Enable Pay Club Directly</span>
          <span className={styles.settingDescription}>
            When enabled, owners may pay the club directly (cash, cheque, or e-transfer) instead
            of paying online. Their entry will be held until the club confirms payment.
          </span>
          {!clubHasAccess && (
            <span className={styles.lockedNote}>
              Pay Club Directly is not enabled for your club. Contact support to enable this feature.
            </span>
          )}
        </div>
        <div className={styles.settingControl}>
          <Checkbox
            id="pay_club_directly_enabled"
            label=""
            value={clubHasAccess ? payClubDirectlyEnabled : false}
            disabled={!clubHasAccess}
            onChange={(e) => setPayClubDirectlyEnabled(e.target.checked)}
          />
        </div>
      </div>

      {isRegistrationOrder && (
        <div className={styles.settingRow}>
          <div className={styles.settingInfo}>
            <span className={styles.settingLabel}>Auto assign armband numbers</span>
            <span className={styles.settingDescription}>
              When enabled, armband numbers are automatically assigned to dogs in registration
              order when they check in. When disabled, armbands must be assigned manually.
            </span>
          </div>
          <div className={styles.settingControl}>
            <Checkbox
              id="auto_assign_armband_numbers"
              label=""
              value={autoAssignArmbandNumbers}
              onChange={(e) => setAutoAssignArmbandNumbers(e.target.checked)}
            />
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <Button isLoading={saving} onClick={handleSave}>
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default DayOfShowTab;
